import config from '@app/config'

import { getDataLayerPush } from '@app/utils/dataLayer'
import { addBreadcrumb } from '@app/utils/errorReport/errorReport'
import { facebookTrackCustom } from '@app/utils/facebookPixel'
import ga from '@app/utils/ga'
import { createLogger } from '@app/utils/loggerCreator'
import ym from '@app/utils/ym'

import type { AnalyticsProvider } from '@app/packages/analytics/providers'
import type { AnalyticsSink, ResolvedEvent } from '@app/packages/analytics/router'
import { AnalyticsRouter, composeProcessors, flattenPayload, googleEventName, mergeData, onlyKind } from '@app/packages/analytics/router'

import type { Amplitude } from './Amplitude'
import { getUserData } from './analyticsUser'
import type { ApplicationBridge, ApplicationMessageEventProvider } from './ApplicationBridge'
import type { TrackingsServiceInterface } from './TrackingsService'

/**
 * The declared-event path, sending through the same provider calls `AnalyticsEvent` makes. What was
 * an interceptor chain over one event object is now a sink per provider plus one reroute step, so
 * the two mechanisms do not sit beside each other.
 */
export const createAnalyticsRouter = (options: {
  trackings: TrackingsServiceInterface
  amplitude: Amplitude
  /** Only in a webview. Resolved per event, because the bridge lands asynchronously. */
  getBridge?: () => ApplicationBridge | undefined
}) =>
  new AnalyticsRouter({
    sinks: [
      { provider: 'trackings', process: onlyKind('custom'), send: event => options.trackings.sendEvent(event.type, event.data) },
      { provider: 'amplitude', send: event => options.amplitude.logEvent(`${event.kind}.${event.type}`, event.data) },
      { provider: 'sentry', send: sendBreadcrumb },
      // User dimensions ride on these two only, which is where `AnalyticsEvent` has always put them.
      { provider: 'yandex', process: mergeData(getUserData), send: sendYandex },
      { provider: 'google', process: mergeData(getUserData), send: sendGoogle },
      { provider: 'facebook', process: composeProcessors(onlyKind('custom'), flattenPayload), send: sendFacebook },
      { provider: 'dataLayer', process: onlyKind('pageview'), send: sendDataLayer },
    ],
    reroute: options.getBridge && createBridgeReroute(options.getBridge),
  })

/**
 * The app's providers are a second set of destinations, not the same ones — `yandex` is AppMetrica
 * there and Metrika here, `google` is Firebase there and GTM here. So only `trackings`, one shared
 * backend, and `amplitude`, whose app project wins, stop firing web-side.
 */
const createBridgeReroute =
  (getBridge: () => ApplicationBridge | undefined) =>
  (event: ResolvedEvent, providers: readonly AnalyticsProvider[]): readonly AnalyticsProvider[] => {
    const forwarded = providers.filter(isBridgeProvider)
    if (!forwarded.length) return providers

    const bridge = getBridge()
    // Bridge lands asynchronously; until it does, everything has to keep firing web-side.
    if (!bridge) return providers

    bridge.sendMessage({
      type: 'amplitude_event',
      event: `${event.kind}.${event.type}`,
      event_properties: event.data,
      providers: forwarded,
    })

    return providers.filter(provider => provider !== 'amplitude' && provider !== 'trackings')
  }

const isBridgeProvider = (provider: AnalyticsProvider): provider is ApplicationMessageEventProvider =>
  provider === 'yandex' || provider === 'google' || provider === 'amplitude' || provider === 'sentry' || provider === 'trackings'

const sendBreadcrumb: AnalyticsSink['send'] = event =>
  addBreadcrumb({
    category: event.kind === 'pageview' ? 'pageview' : 'log',
    message: event.type,
    level: 'info',
    data: event.kind === 'pageview' ? { url: event.url, path: event.path, ...event.data } : event.data,
  })

const sendYandex: AnalyticsSink['send'] = event => {
  if (event.kind === 'pageview') ym('hit', event.url, { title: event.title })
  else ym('reachGoal', event.type, event.data)
}

const sendGoogle: AnalyticsSink['send'] = event => {
  if (event.kind === 'pageview') {
    ga('send', 'pageview', { title: event.title, page: event.url })
    return
  }
  // The sanitised name is what GA4 reads; the one that was declared rides along beside it.
  pushToDataLayer('event', { event: 'custom_event', event_name: googleEventName(event.type), event_id: event.type, ...event.data })
}

/** User dimensions land last here, unlike everywhere else — the shape GTM has always been given. */
const sendDataLayer: AnalyticsSink['send'] = event => {
  if (event.kind !== 'pageview') return
  pushToDataLayer('pageview', { event: 'custom_pageview', event_id: event.type, ...event.data, ...getUserData() })
}

const sendFacebook: AnalyticsSink['send'] = event => {
  // The pixel takes scalars only, and a nullish parameter says nothing it could record.
  const entries = Object.entries(event.data).filter((entry): entry is [string, string | number | boolean] => entry[1] !== null && entry[1] !== undefined)
  const data = entries.length ? Object.fromEntries(entries) : undefined

  if (config.facebookPixelId) facebookTrackCustom(config.facebookPixelId, event.type, data)
  else if (data) facebookLog.log(event.type, data)
  else facebookLog.log(event.type)
}

const pushToDataLayer = (label: string, layer: Record<string, unknown>) => {
  const push = getDataLayerPush()
  if (push) push(layer)
  else dataLayerLog.log(label, layer)
}

const dataLayerLog = createLogger('dataLayer.push', 'lightpink', undefined, true)
const facebookLog = createLogger('facebookTrack', 'lightpink', undefined, true)
